import { IWasmCustomData, TRoutePopupInfoColorVariant } from "~/interfaces";
import { TEXTURES_CONF, WASM_TEXTURES } from "./const";
import { ICustomTexture } from "./interfaces";
import { getImage } from "./utils";
import getWasmTexturesData, { TWasmTextureNames } from "./textures-data";

/**
 * Ziskani metadat - prepocty, anchor...
 */
export function getTextureMetaData(name: TWasmTextureNames) {
	let width = WASM_TEXTURES.defSize;
	let height = WASM_TEXTURES.defSize;
	let anchorX = 0;
	let anchorY = 0;
	const data = getWasmTexturesData(name);

	if (data) {
		// retina
		width = data.width / WASM_TEXTURES.ratio;
		height = data.height / WASM_TEXTURES.ratio;
		anchorX = width / 2;
		anchorY = height / 2;

		// pridat vse, co neni kruh
		switch (name) {
			// u kapek to proste nesedi kvuli stinu
			case "big-active":
			case "big-inactive":
			case "big-universal-active":
			case "big-universal-inactive":
				anchorX = 24;
				anchorY = height;
				break;

			case "small-active":
			case "small-inactive":
			case "small-universal-active":
			case "small-universal-inactive":
				anchorX = 16;
				anchorY = height;
				break;

			case "small-gray":
				anchorX = 24;
				anchorY = 41;
				break;

			case "little-active":
			case "little-inactive":
				anchorX = 7;
				anchorY = height;
				break;

			case "pano":
				anchorY = height - 8;
				break;

			default:
		}
	}

	return {
		data,
		width,
		height,
		anchorX,
		anchorY,
	};
}

/**
 * Rozdel text s fontem do n radku
 */
function splitTextToLines(ctx: CanvasRenderingContext2D, text: string, maxCanvasWidth: number, maxLines?: number) {
	const lines: Array<string> = [];
	const metrics = ctx.measureText(text);
	let maxWidth = metrics.width;

	if (metrics.width > maxCanvasWidth) {
		// splitneme na mezery a carky
		const words = text.match(/[^\s-]+|[-]/gu) || [];
		let currentWords = [];

		maxWidth = 0;

		for (const word of words) {
			const currentWord = [...currentWords, word].join(" ");
			const curMetrics = ctx.measureText(currentWord);

			if (curMetrics.width > maxCanvasWidth) {
				lines.push(currentWords.join(" "));
				currentWords = [word];
			} else {
				maxWidth = Math.max(maxWidth, curMetrics.width);
				currentWords.push(word);
			}
		}

		if (currentWords.length > 0) {
			lines.push(currentWords.join(" "));
		}
	} else {
		lines.push(text);
	}

	// orezeme
	if (typeof maxLines === "number" && lines.length > maxLines) {
		lines.length = maxLines;
		lines[maxLines - 1] += "...";
	}

	return {
		lines,
		maxWidth,
	};
}

/**
 * Pomocna fce pro kresleni zaoblenych rohu
 */
function drawRoundedRect(ctx: CanvasRenderingContext2D, xPos: number, yPos: number, width: number, height: number, radius: number) {
	ctx.beginPath();
	ctx.moveTo(xPos + radius, yPos);
	ctx.lineTo(xPos + width - radius, yPos);
	ctx.quadraticCurveTo(xPos + width, yPos, xPos + width, yPos + radius);
	ctx.lineTo(xPos + width, yPos + height - radius);
	ctx.quadraticCurveTo(xPos + width, yPos + height, xPos + width - radius, yPos + height);
	ctx.lineTo(xPos + radius, yPos + height);
	ctx.quadraticCurveTo(xPos, yPos + height, xPos, yPos + height - radius);
	ctx.lineTo(xPos, yPos + radius);
	ctx.quadraticCurveTo(xPos, yPos, xPos + radius, yPos);
	ctx.closePath();
}

/**
 * Ziskani kruhu pro vykresleni geo lokace/sdileni polohy
 */
export function getCircle(color: string, size: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const radiusHalf = size / 2;

	newCanvas.width = size;
	newCanvas.height = size;
	ctx.beginPath();
	ctx.fillStyle = color;
	ctx.arc(radiusHalf, radiusHalf, radiusHalf, 0, Math.PI * 2);
	ctx.fill();

	return {
		imageData: ctx.getImageData(0, 0, size, size),
		canvas: newCanvas,
	};
}

/**
 * Ziskani textu pro search markery
 */
export function getText(text: string, width = WASM_TEXTURES.searchMarkersWidth): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const outlineSize = 2 * WASM_TEXTURES.ratio;
	// zvetseni sirky
	const incWidth = 5;
	const maxLines = 2;
	const fontSize = 24;

	// retina
	newCanvas.width = width * WASM_TEXTURES.ratio;

	const maxCanvasWidth = newCanvas.width - incWidth;
	const font = `bold ${fontSize}px arial`;

	ctx.font = font;
	ctx.lineWidth = outlineSize * WASM_TEXTURES.ratio;

	const { lines, maxWidth } = splitTextToLines(ctx, text, maxCanvasWidth, maxLines);

	newCanvas.width = (maxWidth >>> 0) + incWidth;
	newCanvas.height = WASM_TEXTURES.textLineHeight * lines.length;
	ctx.font = font;

	for (let ind = 0, max = lines.length; ind < max; ind++) {
		const curText = lines[ind];
		const xPos = incWidth / 2;
		const yPos = (ind + 1) * fontSize;

		// tloustka obrysu
		ctx.lineWidth = outlineSize * 2;
		ctx.lineJoin = "round";
		ctx.lineCap = "round";
		// barva obrysu
		ctx.strokeStyle = "white";
		ctx.strokeText(curText, xPos, yPos);
		ctx.fillStyle = "black";
		ctx.fillText(curText, xPos, yPos);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Pomocna metoda pro vykresleni zaobleneho obdelniku
 */
function roundedFilledRect(ctx: CanvasRenderingContext2D, rectX: number, rectY: number, rectWidth: number, rectHeight: number, cornerRadius: number, color: string) {
	// Set faux rounded corners
	ctx.lineJoin = "round";
	ctx.lineWidth = cornerRadius;
	ctx.strokeStyle = color;
	ctx.fillStyle = color;

	// Change origin and dimensions to match true size (a stroke makes the shape a bit larger)
	ctx.strokeRect(rectX + (cornerRadius / 2), rectY + (cornerRadius / 2), rectWidth - cornerRadius, rectHeight - cornerRadius);
	ctx.fillRect(rectX + (cornerRadius / 2), rectY + (cornerRadius / 2), rectWidth - cornerRadius, rectHeight - cornerRadius);
}

/**
 * Ziskani hodnoceni pro search markery
 */
export function getReview(value: number, isActive: boolean): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const width = 22 * WASM_TEXTURES.ratio;
	const height = 22 * WASM_TEXTURES.ratio;
	const roundedCorner = 4 * WASM_TEXTURES.ratio;
	const colors = isActive
		? {
			primary: "#fff",
			secondary: "#1eae00",
		}
		: {
			primary: "#6D6D6D",
			secondary: "#fff",
		};
	const xPos = 0;
	const yPos = 0;
	const offset = (isActive ? 1 : 0.5) * WASM_TEXTURES.ratio;

	newCanvas.width = width;
	newCanvas.height = height;
	roundedFilledRect(ctx, xPos, yPos, width, height, roundedCorner, colors.primary);
	roundedFilledRect(ctx, xPos + offset, yPos + offset, width - (offset * 2), height - (offset * 2), roundedCorner, colors.secondary);

	ctx.font = "bold 24px arial";
	ctx.fillStyle = colors.primary;
	ctx.fillText(value.toFixed(1), xPos + 5, yPos + WASM_TEXTURES.textLineHeight);

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Ziskani obrazku pro sdileni polohy
 */
export async function getCircleImage(url: string, size: number, isGray: boolean): Promise<ICustomTexture> {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");

	newCanvas.width = size;
	newCanvas.height = size;

	try {
		const img = await getImage(url, "anonymous");
		const radius = size / 2;
		const imgWidth = img.naturalWidth;
		const imgHeight = img.naturalHeight;
		let posX = 0;
		let posY = 0;
		let sourceWidth = imgWidth;
		let sourceHeight = imgHeight;

		// landscape
		if (imgWidth > imgHeight) {
			posX = (imgWidth - imgHeight) / 2;
			sourceWidth = imgHeight;
		// portrait
		} else if (imgHeight > imgWidth) {
			posY = (imgHeight - imgWidth) / 2;
			sourceHeight = imgWidth;
		}

		ctx.beginPath();
		ctx.arc(radius, radius, radius, 0, Math.PI * 2, true);
		ctx.closePath();
		ctx.clip();

		if (isGray) {
			ctx.filter = "grayscale(1)";
		}

		ctx.drawImage(img, posX, posY, sourceWidth, sourceHeight, 0, 0, size, size);
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - getCircleImage exception ${exc.message}`);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Batuzek - popisky geometrii
 */
export function getPathCaption(text: string): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");

	const maxCanvasWidth = 100 * WASM_TEXTURES.ratio;
	const shadowLen = 2 * WASM_TEXTURES.ratio;
	const fontSize = 12 * WASM_TEXTURES.ratio;
	const lineHeight = 14 * WASM_TEXTURES.ratio;
	const padding = 8;

	ctx.font = `bold ${fontSize}px arial`;

	const { lines, maxWidth } = splitTextToLines(ctx, text, maxCanvasWidth - (padding * 2), 3);
	const height = (padding * WASM_TEXTURES.ratio) + (lineHeight * lines.length);
	const width = maxWidth + (2 * WASM_TEXTURES.ratio * padding);

	newCanvas.width = width + shadowLen;
	newCanvas.height = height + shadowLen;
	ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
	ctx.shadowBlur = shadowLen;
	ctx.shadowOffsetX = shadowLen / 2;
	ctx.shadowOffsetY = shadowLen / 2;

	// padding 6 8 6
	// 30 vyska jednoho, 54 u 3., line height 14px

	ctx.fillStyle = "#ffffff"; // rectangle color
	drawRoundedRect(ctx, 1, 1, width, height, 4 * WASM_TEXTURES.ratio);
	ctx.globalAlpha = 0.8;
	ctx.fill();
	ctx.globalAlpha = 1;

	ctx.shadowColor = "transparent";
	ctx.strokeStyle = "#000000";
	ctx.lineWidth = 2 * WASM_TEXTURES.ratio;
	drawRoundedRect(ctx, 1, 1, width, height, 4 * WASM_TEXTURES.ratio);
	ctx.globalAlpha = 0.56;
	ctx.stroke();

	ctx.globalAlpha = 1;
	ctx.fillStyle = "#000";
	ctx.font = `bold ${fontSize}px arial`;

	let posY = padding * WASM_TEXTURES.ratio * 2;

	for (const line of lines) {
		const metrics = ctx.measureText(line);

		ctx.fillText(line, (width - metrics.width) / 2, posY);
		posY += lineHeight;
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Fce pro konvertovani svg -> canvas
 */
export async function svgToCanvas(svgUrl: string, ratio = WASM_TEXTURES.ratio, forceFill?: string): Promise<HTMLCanvasElement | null> {
	try {
		const data = await fetch(svgUrl);
		let text = await data.text();

		// doplnime hlavicku
		if (!text.includes("xmlns")) {
			text = text.replace("<svg", `<svg xmlns="http://www.w3.org/2000/svg" `);
		}

		if (forceFill) {
			const fillMatches = text.match(/fill="[^"]+"/ug);

			if (fillMatches) {
				for (const match of fillMatches) {
					text = text.replace(match, `fill="${forceFill}"`);
				}
			}
		}

		const img = await getImage(`data:image/svg+xml;base64,${btoa(text)}`);
		const viewBox = text.match(/viewBox\s*=\s*"([^"]+)/u);
		let width = img.naturalWidth;
		let height = img.naturalHeight;

		// pouzijeme rozmery z view boxu
		if (viewBox) {
			const parts = viewBox[1].split(" ");

			if (parts.length === 4) {
				width = parseFloat(parts[2]);
				height = parseFloat(parts[3]);
			}
		}

		width *= ratio;
		height *= ratio;

		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");

		newCanvas.width = width;
		newCanvas.height = height;

		ctx.drawImage(img, 0, 0, width, height);

		return newCanvas;
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - svgToCanvas exception ${exc.message}`);

		return null;
	}
}

/**
 * Kolecko pro routovac - start/cil/prujezdni body
 */
export function getRoutePointCircle(radius: number, color: string, bgColor = "#ffffff"): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	// puvodne 8
	const size = radius * 2;

	newCanvas.width = size * WASM_TEXTURES.ratio;
	newCanvas.height = size * WASM_TEXTURES.ratio;
	ctx.lineWidth = WASM_TEXTURES.ratio;
	ctx.arc(size, size, size - WASM_TEXTURES.ratio + 1, 0, Math.PI * 2);
	ctx.fillStyle = bgColor;
	ctx.strokeStyle = color;
	ctx.fill();
	ctx.globalAlpha = 0.6;
	ctx.stroke();

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export async function getCenteredIcon(url: string, size: number, iconSize: number): Promise<ICustomTexture> {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");

	newCanvas.width = size * WASM_TEXTURES.ratio;
	newCanvas.height = size * WASM_TEXTURES.ratio;

	try {
		const img = await getImage(url, "anonymous");
		const imgSize = WASM_TEXTURES.ratio * iconSize;
		const top = (newCanvas.width - imgSize) / 2;
		const left = (newCanvas.height - imgSize) / 2;

		ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, top, left, imgSize, imgSize);
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - getCenteredIcon exception ${exc.message}`);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export function getTextMetrics(ctx: CanvasRenderingContext2D, text: string, font: string) {
	ctx.font = font;

	const metrics = ctx.measureText(text);
	const fontSize = parseFloat(font.match(/\d+/u)[0]);

	return {
		width: metrics.width,
		height: Math.max(fontSize, Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent)),
	};
}

export function createRoutePopupContentLines(lines: IWasmCustomData["routePopupInfo"], variant: TRoutePopupInfoColorVariant): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const fontSizes = [14 * WASM_TEXTURES.ratio, 16 * WASM_TEXTURES.ratio];
	const betweenTexts = 5 * WASM_TEXTURES.ratio;
	let width = 0;
	let posY = 0;
	const posYItems = [];

	for (const line of lines) {
		const fontSize = line.bold ? fontSizes[1] : fontSizes[0];
		const measure = getTextMetrics(ctx, line.text, `${line.bold ? "bold " : ""}${fontSize}px arial`);

		width = Math.max(width, measure.width);
		posY += measure.height;
		posYItems.push(posY);
		posY += betweenTexts;
	}

	newCanvas.width = width;
	newCanvas.height = posY;

	//ctx.fillStyle = "#ccc";
	//ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);

	let ind = 0;

	for (const line of lines) {
		const fontSize = line.bold ? fontSizes[1] : fontSizes[0];

		ctx.font = `${line.bold ? "bold " : ""}${fontSize}px arial`;
		ctx.fillStyle = variant === "active"
			? line.activeColor || TEXTURES_CONF.routePopupActiveColor
			: line.inactiveColor || TEXTURES_CONF.routePopupInactiveColor;
		ctx.fillText(line.text, 0, posYItems[ind]);
		ind++;
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/** Textura bez ikonky */
export function getWeatherIconTextureCanvas(hasIcon: boolean, text: string, time: string, textBold?: boolean) {
	const newCanvas = document.createElement("canvas");
	const iconSize = 24 * WASM_TEXTURES.ratio;
	const padding = 5 * WASM_TEXTURES.ratio;
	const ctx = newCanvas.getContext("2d");
	const tempDef = [`${textBold ? "bold " : ""}${14 * WASM_TEXTURES.ratio}px arial`, "#000000"];
	const timeDef = [`${12 * WASM_TEXTURES.ratio}px arial`, "#828282"];

	// nelze spocitat, musime zadratovat
	const tempMeas = getTextMetrics(ctx, text, tempDef[0]);
	const timeMeas = getTextMetrics(ctx, time, timeDef[0]);
	const firstTextOffset = hasIcon ? iconSize + padding : 0;
	const textPos = [firstTextOffset, firstTextOffset + tempMeas.width + padding];
	const width = textPos[1] + timeMeas.width;

	newCanvas.width = width;
	newCanvas.height = iconSize;

	// teplota
	ctx.font = tempDef[0];
	ctx.fillStyle = tempDef[1];
	ctx.fillText(text, textPos[0], newCanvas.height - (tempMeas.height / 2));

	// cas
	ctx.font = timeDef[0];
	ctx.fillStyle = timeDef[1];
	ctx.fillText(time, textPos[1], newCanvas.height - (timeMeas.height / 2) - 2);

	return {
		canvas: newCanvas,
		ctx,
	};
}

/** Cela textura */
export function getWeatherIconTexture(text: string, time: string, textBold: boolean, icon?: HTMLCanvasElement): ICustomTexture {
	const { canvas: newCanvas, ctx } = getWeatherIconTextureCanvas(!!icon, text, time, textBold);

	if (icon) {
		// ikonka
		ctx.drawImage(icon, 0, 0);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}
